/**
 * Native OAuth return (ALU-519).
 *
 * In the Capacitor shell the PKCE code verifier cookie of "Entrar con
 * LinkedIn" lives in the WebView's cookie jar, while an in-app browser
 * (SFSafariViewController / Custom Tabs) has its own jar. So the round trip
 * is split in two:
 *
 *  1. the in-app browser runs LinkedIn and lands on
 *     `/auth/callback?native=1&code=…` (app/auth/callback/route.ts), which
 *     does NOT exchange the code: it bounces to
 *     `alumnia://auth/callback?code=…` (`nativeAuthReturnUrl`);
 *  2. the app receives that link (lib/utils/deep-linking.ts), closes the
 *     in-app browser and loads `/auth/callback?code=…` inside the WebView
 *     (`nativeAuthReturnTarget`), where the verifier cookie is, and the
 *     exchange runs as on the web.
 *
 * It only starts when the native binary has the Browser plugin
 * (`isInAppBrowserAvailable`); apps built without it keep the old behavior.
 */

import { isNativePlatform } from '@/lib/utils/platform'

/** Query flag set on the callback URL of a flow that runs in the in-app browser. */
export const NATIVE_AUTH_FLAG = 'native'

/** Params that travel through the custom-scheme hop; anything else is dropped. */
const RETURN_PARAMS = ['code', 'next', 'org', 'flow', 'error'] as const

const CUSTOM_SCHEME = 'alumnia'
const RETURN_PATH = '/auth/callback'
const MAX_PARAM_LENGTH = 1024

type BrowserPlugin = {
  open: (options: { url: string }) => Promise<void>
  close?: () => Promise<void>
}

function browserPlugin(): BrowserPlugin | null {
  if (typeof window === 'undefined') return null
  const cap = (
    window as unknown as {
      Capacitor?: {
        isPluginAvailable?: (name: string) => boolean
        Plugins?: Record<string, BrowserPlugin | undefined>
      }
    }
  ).Capacitor
  try {
    if (!cap?.isPluginAvailable?.('Browser')) return null
    return cap.Plugins?.Browser ?? null
  } catch {
    return null
  }
}

/** True only in a native shell whose binary includes the Browser plugin. */
export function isInAppBrowserAvailable(): boolean {
  return isNativePlatform() && browserPlugin() !== null
}

/** Open `url` in the in-app browser. False when it could not be opened. */
export async function openInAppBrowser(url: string): Promise<boolean> {
  const plugin = browserPlugin()
  if (!plugin) return false
  try {
    await plugin.open({ url })
    return true
  } catch {
    return false
  }
}

/** Close the in-app browser (a no-op on Android, where it is not implemented). */
export async function closeInAppBrowser(): Promise<void> {
  try {
    await browserPlugin()?.close?.()
  } catch {
    // Best effort: the app is already in the foreground.
  }
}

function pickParams(source: URLSearchParams): URLSearchParams {
  const picked = new URLSearchParams()
  for (const key of RETURN_PARAMS) {
    const value = source.get(key)
    if (value && value.length <= MAX_PARAM_LENGTH) picked.set(key, value)
  }
  return picked
}

/**
 * `alumnia://auth/callback?…` the callback route redirects to, built from the
 * in-app browser's callback query. Null when there is nothing to hand back.
 */
export function nativeAuthReturnUrl(query: URLSearchParams): string | null {
  const picked = pickParams(query)
  if (!picked.has('code') && !picked.has('error')) return null
  return `${CUSTOM_SCHEME}://auth/callback?${picked.toString()}`
}

/**
 * The same-origin path the WebView loads for an incoming
 * `alumnia://auth/callback?…` link, or null when `url` is anything else.
 * The path is fixed and only the whitelisted params are copied, so the link
 * cannot steer the WebView anywhere else; `next` is validated again by the
 * callback route itself.
 */
export function nativeAuthReturnTarget(url: string): string | null {
  if (typeof url !== 'string' || url.length === 0 || url.length > 4096) return null
  const match = /^alumnia:\/\/auth\/callback\/?(?:\?([^#]*))?(?:#.*)?$/i.exec(url)
  if (!match) return null
  const picked = pickParams(new URLSearchParams(match[1] ?? ''))
  if (!picked.has('code') && !picked.has('error')) return null
  return `${RETURN_PATH}?${picked.toString()}`
}
